import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { Resource, PaginationModel } from "../..";
import { HttpService } from "../http.service";

export { HttpService };

export interface PaginatedResult<T> {
  data: T[];
  filters: any[];
  paging: PaginationModel;
}

export interface PagedArray<T> extends Array<T> {
  filters: any[];
  paging: PaginationModel;
}

export const baseApiUrl: string = environment.wimsApi;

export function resourceUrlFor(resourceName: string): string {
  return `${baseApiUrl}${resourceName}`;
}

@Injectable()
export class BaseDataService<T extends Resource> {
  resourceUrl: string;

  constructor(public http: HttpService, public resourceName: string) {
    this.resourceUrl = resourceUrlFor(resourceName);
  }

  runCommand(cmd: string, model?: any): Observable<any> {
    return this.http.request(`${this.resourceUrl}/${cmd}`, {
      method: "POST",
      payload: model,
    });
  }

  apiResourceRequest(method: string, url: string, payload: any): Observable<T> {
    return this.http
      .request(url, { method, payload })
      .pipe(map((response: Response) => this.processSingleEntity(response)));
  }

  toHttpParams(filters: { [key: string]: any }): HttpParams {
    return this.http.buildHttpParams(filters);
  }

  downloadExcel(searchCriteria: string) {
    return this.download(
      this.toHttpParams({
        search: searchCriteria,
        pageSize: -1,
        format: "xlsx",
      })
    );
  }

  download(params: HttpParams): void {
    window.open(`${this.resourceUrl}?${params.toString()}`);
  }

  all(searchParams?: any): Observable<PagedArray<T>> {
    let res = this.http.request(this.resourceUrl, { searchParams });
    return res.pipe(map((response) => this.processArrayOfEntities(response)));
  }

  get(id: any): Observable<T> {
    return this.http
      .request(this.urlForSingleEntity(id))
      .pipe(map((response: Response) => this.processSingleEntity(response)));
  }

  urlForSingleEntity(id: any) {
    let dynamicRandomValue = new Date().getTime().toString();
    return `${this.resourceUrl}/${id}?v=${dynamicRandomValue}`;
  }

  add(resource: T, qty: number = 1): Observable<T> {
    return this.apiResourceRequest(
      "POST",
      qty > 1 ? this.resourceUrl + `?qty=${qty}` : this.resourceUrl,
      resource
    );
  }

  update(resource: T): Observable<T> {
    return this.apiResourceRequest("PUT", this.resourceUrl, resource);
  }

  save(resource: T, qty: number = 1): Observable<T> {
    return resource.id ? this.update(resource) : this.add(resource, qty);
  }

  remove(id: any): Observable<Response> {
    return this.http.request(`${this.resourceUrl}/${id}`, { method: "DELETE" });
  }

  processSingleEntity(response: Response): T {
    return this.createInstance(response);
  }

  processArrayOfEntities(response: Response, activator?: (options?: any) => T): PagedArray<T> {
    let factory = activator || this.createInstance.bind(this),
      result = response as unknown as PaginatedResult<T>;

    let list = result.data.map((x) => factory(x));
    let paging = result.paging;
    let filters = result.filters;

    return Object.assign(list, { filters, paging });
  }

  createInstance(options?: any): T {
    return options;
  }
}
